import {
	createContext,
	type ReactNode,
	useCallback,
	useMemo,
	useState,
} from 'react';

import { useRequiredContext } from 'hooks';

interface PopoverProviderProps {
	onClose: () => void;
	resetShouldCloseOnOutsideClick: (shouldClose?: boolean) => void;
	setShouldCloseOnOutsideClick: (shouldClose: boolean) => void;
	shouldCloseOnOutsideClick: boolean;
}

const PopoverContext = createContext<PopoverProviderProps | undefined>(
	undefined,
);

interface Props {
	children: ReactNode;

	/** Callback function when the popover should close. */
	onClose: () => void;

	/** If the popover should close when clicking on the background outside it. */
	shouldCloseOnOutsideClick?: boolean;
}

export default function PopoverContextProvider({
	children,
	onClose,
	shouldCloseOnOutsideClick: shouldCloseOnOutsideClickProp = true,
}: Props) {
	const [shouldCloseOnOutsideClick, setShouldCloseOnOutsideClick] = useState(
		shouldCloseOnOutsideClickProp,
	);

	const resetShouldCloseOnOutsideClick = useCallback(
		(shouldClose?: boolean) => {
			setShouldCloseOnOutsideClick(
				shouldClose ?? shouldCloseOnOutsideClickProp,
			);
		},
		[shouldCloseOnOutsideClickProp],
	);

	const value = useMemo(
		() => ({
			shouldCloseOnOutsideClick,
			setShouldCloseOnOutsideClick,
			onClose,
			resetShouldCloseOnOutsideClick,
		}),
		[shouldCloseOnOutsideClick, onClose, resetShouldCloseOnOutsideClick],
	);

	return <PopoverContext value={value}>{children}</PopoverContext>;
}
PopoverContextProvider.displayName = 'PopoverContextProvider';

export function usePopoverContext() {
	return useRequiredContext(PopoverContext, 'usePopoverContext');
}
